import { createReducer, AnyAction, createAsyncThunk, current } from '@reduxjs/toolkit';
import { notificationService } from 'services/notificationService';
import * as actions from 'actionCreators';
import { SelectedTargetService, TeamMembershipType, DefaultBoard } from 'types';
import * as ProfileTypes from 'ProfileModule/types';
import { ProvisionErrors } from 'IntegrationModule/api/provisionIntegrationApi';

type InitialStateType = {
  provisionErrors: ProvisionErrors;
  team_members: Array<TeamMembershipType>;
  // Total members matching the current server-side search (GLX-1740). When
  // `team_members` holds a single page (All Members tab), this is the full count.
  team_members_total_count: number;
  selectedTargetService: SelectedTargetService;
  agentToken: ProfileTypes.AgentToken;
  // integrationConnect: ProfileTypes.IntegrationConnection;
  requestStatusesAndErrors: {
    [x: string]: {
      isRequesting: any;
      error: any;
      isSuccess?: boolean;
    };
  };
  default_board: DefaultBoard | object;
};

export const initialState: InitialStateType = {
  team_members: [],
  team_members_total_count: 0,
  default_board: {},
  selectedTargetService: {
    name: '',
    id: '',
    pendingEntitiesDataTypes: [],
    pendingEntitiesMappingStatuses: [],
  },
  agentToken: '',
  // integrationConnection: {}, TO COME
  provisionErrors: [],
  requestStatusesAndErrors: {
    // Example - Using string literals to avoid circular dependency
    fetchTeamMembers: {
      isRequesting: false,
      error: {},
    },
    updateTeamMember: {
      isRequesting: false,
      error: {},
    },
    updateTeam: {
      isRequesting: false,
      error: {},
    },
    searchTeamMembers: {
      isRequesting: false,
      error: {},
    },
  },
};

export default createReducer(initialState, (builder) => {
  /* ---------------------------- Non Async Request --------------------------- */

  builder.addCase(actions.setTargetService, (state, { payload }) => {
    state.selectedTargetService = payload;
  });

  builder.addCase(actions.updateProfileState, (state, { payload }) => {
    const nextState = { ...state };
    Object.keys(payload).forEach((reduxKey) => {
      (nextState as Record<string, unknown>)[reduxKey] = payload[reduxKey];
    });
    return nextState;
  });

  /* --------------------------- Fetch Team Members --------------------------- */
  builder.addMatcher(
    (action) => action.type === 'fetchTeamMembers/pending',
    (state, action: any) => {
      state.requestStatusesAndErrors.fetchTeamMembers = {
        isRequesting: true,
        error: {},
      };
    },
  );

  builder.addMatcher(
    (action) => action.type === 'fetchTeamMembers/fulfilled',
    (state, action: any) => {
      state.requestStatusesAndErrors.fetchTeamMembers = {
        isRequesting: false,
        error: {},
      };
      state.team_members = action.payload;
    },
  );
  builder.addMatcher(
    (action) => action.type === 'fetchTeamMembers/rejected',
    (state, action: any) => {
      const rawError = action.payload || action.error;
      const statusCode = rawError?.response?.status;
      const errorMessage =
        statusCode === 504
          ? 'Request timed out.'
          : statusCode === 500
            ? 'Server error - please try again later.'
            : rawError?.response?.data?.message ||
              rawError?.message ||
              'Failed to load team members';

      state.requestStatusesAndErrors.fetchTeamMembers = {
        isRequesting: false,
        error: {
          message: errorMessage,
          statusCode,
          name: rawError?.name,
          code: rawError?.code,
        },
      };
      // Keep existing team_members data on error
    },
  );

  /* ---------------------- Search Team Members (paginated) ---------------------- */
  builder.addMatcher(
    (action) => action.type === 'searchTeamMembers/pending',
    (state) => {
      state.requestStatusesAndErrors.searchTeamMembers = {
        isRequesting: true,
        error: {},
      };
    },
  );
  builder.addMatcher(
    (action) => action.type === 'searchTeamMembers/fulfilled',
    (state, action: any) => {
      state.requestStatusesAndErrors.searchTeamMembers = {
        isRequesting: false,
        error: {},
      };
      state.team_members = action.payload.teamMembers;
      state.team_members_total_count = action.payload.totalCount;
    },
  );
  builder.addMatcher(
    (action) => action.type === 'searchTeamMembers/rejected',
    (state, action: any) => {
      const rawError = action.payload || action.error;
      const statusCode = rawError?.response?.status;
      const errorMessage =
        statusCode === 504
          ? 'Request timed out.'
          : statusCode === 500
            ? 'Server error - please try again later.'
            : rawError?.response?.data?.message ||
              rawError?.message ||
              'Failed to load team members';

      state.requestStatusesAndErrors.searchTeamMembers = {
        isRequesting: false,
        error: {
          message: errorMessage,
          statusCode,
          name: rawError?.name,
          code: rawError?.code,
        },
      };
      // Keep existing team_members data on error
    },
  );

  /* --------------------------- Update Team Member --------------------------- */
  builder.addMatcher(
    (action) => action.type === 'updateTeamMember/pending',
    (state, action: any) => {
      state.requestStatusesAndErrors.updateTeamMember = {
        isRequesting: true,
        error: {},
      };
    },
  );

  builder.addMatcher(
    (action) => action.type === 'updateTeamMember/fulfilled',
    (state, action: any) => {
      const { arg } = action.meta;
      const { teamMembershipId } = arg;
      const updatedMember = action.payload.team_member?.account;
      if (!updatedMember) return state;

      state.requestStatusesAndErrors.updateTeamMember = {
        isRequesting: false,
        error: action.payload.data?.error,
      };

      state.team_members = state.team_members.map((team_member) =>
        team_member.team_membership_id === teamMembershipId
          ? { ...team_member, ...updatedMember, id: teamMembershipId }
          : team_member,
      );
    },
  );
  builder.addMatcher(
    (action) => action.type === 'updateTeamMember/rejected',
    (state, action: any) => {
      state.requestStatusesAndErrors.updateTeamMember = {
        isRequesting: false,
        error: action.error,
      };
    },
  );
  /* ------------------------------- Update Team ------------------------------- */
  builder.addMatcher(
    (action) => action.type === 'updateTeam/pending',
    (state, action: any) => {
      state.requestStatusesAndErrors.updateTeam = {
        isRequesting: true,
        error: {},
      };
    },
  );
  builder.addMatcher(
    (action) => action.type === 'updateTeam/rejected',
    (state, action: any) => {
      state.requestStatusesAndErrors.updateTeam = {
        isRequesting: false,
        error: action.error,
      };
    },
  );
  /* ------------------------------- Reset Members Password ------------------------------- */
  builder.addMatcher(
    (action) => action.type === 'resetMemberPassword/fulfilled',
    (state, action: any) => {
      state.requestStatusesAndErrors.resetMemberPassword = {
        isRequesting: false,
        error: {},
      };
    },
  );

  /* ------------------------------- Bulk Activate Accounts ------------------------------- */
  builder.addMatcher(
    (action) => action.type === 'bulkActivateAccounts/pending',
    (state, action: any) => {},
  );
  builder.addMatcher(
    (action) => action.type === 'bulkActivateAccounts/fulfilled',
    (state, action: any) => {
      const { activated_account_ids, failed_account_ids } = action.payload as any;
      const successfullyActivateIdsText = activated_account_ids?.length
        ? `Accounts with successful activation: ${activated_account_ids.join(', ')}`
        : '';
      const failedActivateIdsText = failed_account_ids?.length
        ? `Accounts failed to activate: ${failed_account_ids.join(', ')}`
        : '';

      // ✅ FIX: Replace alert() with toast notification
      if (activated_account_ids?.length && failed_account_ids?.length) {
        notificationService.warning(
          `${successfullyActivateIdsText}\n${failedActivateIdsText}`,
          6000,
        );
      } else if (activated_account_ids?.length) {
        notificationService.success(successfullyActivateIdsText);
      } else if (failed_account_ids?.length) {
        notificationService.error(failedActivateIdsText);
      }

      const activatedAccountIdsSet = new Set(activated_account_ids);

      if (activatedAccountIdsSet.size) {
        state.team_members = state.team_members.map((member) =>
          activatedAccountIdsSet.has(member.account_id) ? { ...member, is_active: true } : member,
        );
      }
    },
  );
  builder.addMatcher(
    (action) => action.type === 'bulkActivateAccounts/rejected',
    (state, action: any) => {
      // ✅ FIX: Replace alert() with toast notification
      notificationService.error('All accounts activation failed. Please try again.');
    },
  );
});
